import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { AlertTriangle, RotateCcw, Search, IndianRupee, PackageX, Check } from 'lucide-react';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import CommonLoader from '../../components/CommonLoader';
import createAuthedAxios from '../../hooks/useAxiosAuth';
import usePermissions from '../../hooks/usePermissions';

const MySwal = withReactContent(Swal);

// Expiry returns to the distributor — where a pharmacy's margin actually leaks.
//
// THE EXPIRING LIST LEADS, not the register of past claims. Stock that expires
// unclaimed is a pure loss: the distributor's credit window closes a fixed
// number of days before expiry, and after that the hospital has bought the goods
// twice. A register of returns already raised is a record; the list of batches
// still claimable is the thing that earns money, so it is what opens.
//
// Raising a claim MOVES STOCK. Posting decrements the named batch through the
// same atomic path the counter uses, so a batch cannot be claimed twice, and
// cancelling puts it back through the same ledger. Both are recorded against the
// return, so the claim and any reversal sit together in the batch's history.

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const money = (v) => `₹${num(v).toFixed(2)}`;
const dateOnly = (v) => (v ? String(v).slice(0, 10) : '—');

const REASONS = [
  { value: 'expiry', label: 'Expiry' },
  { value: 'damaged', label: 'Damaged' },
  { value: 'wrong_supply', label: 'Wrong supply' },
  { value: 'excess', label: 'Excess' },
  { value: 'other', label: 'Other' },
];

const STATUS_STYLE = {
  draft: 'bg-slate-100 text-slate-600',
  posted: 'bg-amber-100 text-amber-700',
  settled: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-600',
};

export default function PurchaseReturns() {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const axiosInstance = useMemo(() => createAuthedAxios(), []);

  const [tab, setTab] = useState('expiring');
  const [days, setDays] = useState(90);
  const [expiring, setExpiring] = useState([]);
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  // batchId -> { qty, reason } for the claim being assembled.
  const [picked, setPicked] = useState({});
  const [saving, setSaving] = useState(false);

  const mayReturn = can('pharmacy.return') || can('pharmacy.purchase.manage');

  const loadExpiring = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/pharmacy/purchase-returns/expiring?days=${days}`);
      setExpiring(res?.data?.batches || res?.data?.data || []);
    } catch {
      toast.warn(t('purchasereturns.load_failed', 'Could not load expiring stock'));
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, days, t]);

  const loadReturns = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/pharmacy/purchase-returns?limit=50');
      setReturns(res?.data?.returns || res?.data?.data || []);
    } catch {
      toast.warn(t('purchasereturns.load_failed_register', 'Could not load returns'));
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, t]);

  useEffect(() => {
    if (tab === 'expiring') loadExpiring();
    else loadReturns();
  }, [tab, loadExpiring, loadReturns]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = tab === 'expiring' ? expiring : returns;
    if (!q) return list;
    return list.filter((r) => JSON.stringify(r).toLowerCase().includes(q));
  }, [tab, expiring, returns, search]);

  const pickedTotal = useMemo(
    () => Object.values(picked).reduce((sum, p) => sum + num(p.qty) * num(p.rate), 0),
    [picked]
  );

  const toggle = (b) => {
    setPicked((prev) => {
      const next = { ...prev };
      if (next[b.batchId]) delete next[b.batchId];
      else next[b.batchId] = { qty: num(b.qtyOnHand), rate: num(b.purchaseRate), supplierId: b.supplierId, medicineId: b.medicineId, batchNo: b.batchNo, expiryDate: b.expiryDate };
      return next;
    });
  };

  const setQty = (batchId, qty) =>
    setPicked((prev) => ({ ...prev, [batchId]: { ...prev[batchId], qty } }));

  // Every line in one claim must go to ONE supplier — a debit note is raised
  // against a distributor, not against a shelf.
  const supplierIds = [...new Set(Object.values(picked).map((p) => p.supplierId).filter(Boolean))];

  const raiseClaim = async () => {
    const items = Object.entries(picked)
      .map(([batchId, p]) => ({ batchId, medicineId: p.medicineId, batchNo: p.batchNo, expiryDate: p.expiryDate, qty: num(p.qty), purchaseRate: num(p.rate) }))
      .filter((i) => i.qty > 0);
    if (!items.length) return;
    if (supplierIds.length > 1) {
      MySwal.fire({
        icon: 'info',
        title: t('purchasereturns.one_supplier_title', 'One supplier per claim'),
        text: t('purchasereturns.one_supplier_text', 'A debit note is raised against a single distributor. Select batches from one supplier at a time.'),
        confirmButtonColor: '#4ABDBD',
      });
      return;
    }

    const ask = await MySwal.fire({
      title: t('purchasereturns.confirm_title', 'Raise this claim?'),
      html: `<p style="font-size:14px;color:#64748b">${items.length} batch(es), ${money(pickedTotal)} at purchase rate.</p>
             <p style="font-size:13px;color:#94a3b8;margin-top:8px">Posting removes this stock from the shelf. It can be cancelled, which puts it back.</p>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#4ABDBD',
      cancelButtonColor: '#e5e7eb',
      confirmButtonText: t('purchasereturns.raise', 'Raise claim'),
      cancelButtonText: t('purchasereturns.cancel', 'Cancel'),
    });
    if (!ask.isConfirmed) return;

    setSaving(true);
    try {
      const created = await axiosInstance.post('/pharmacy/purchase-returns', {
        supplierId: supplierIds[0], reason: 'expiry', items,
      });
      const returnId = created?.data?.data?.returnId || created?.data?.returnId;
      if (returnId) await axiosInstance.post(`/pharmacy/purchase-returns/${returnId}/post`);
      toast.success(t('purchasereturns.raised', 'Claim raised and stock removed'));
      setPicked({});
      loadExpiring();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('purchasereturns.raise_failed', 'Could not raise the claim'));
    } finally {
      setSaving(false);
    }
  };

  const cancelReturn = async (row) => {
    const ask = await MySwal.fire({
      title: t('purchasereturns.cancel_title', 'Cancel this return?'),
      input: 'text',
      inputLabel: t('purchasereturns.cancel_reason', 'Reason'),
      inputPlaceholder: t('purchasereturns.cancel_ph', 'e.g. keyed against the wrong batch'),
      text: t('purchasereturns.cancel_text', 'The stock goes back on the shelf and the claim is voided.'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#4ABDBD',
      cancelButtonColor: '#e5e7eb',
      inputValidator: (v) => (!v || !v.trim() ? t('purchasereturns.reason_required', 'A reason is required') : undefined),
    });
    if (!ask.isConfirmed) return;
    try {
      await axiosInstance.post(`/pharmacy/purchase-returns/${row.returnId}/cancel`, { reason: ask.value });
      toast.success(t('purchasereturns.cancelled', 'Return cancelled and stock restored'));
      loadReturns();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('purchasereturns.cancel_failed', 'Could not cancel the return'));
    }
  };

  const settle = async (row) => {
    const ask = await MySwal.fire({
      title: t('purchasereturns.settle_title', 'Record the credit note'),
      input: 'text',
      inputLabel: t('purchasereturns.debit_note', 'Debit / credit note number'),
      showCancelButton: true,
      confirmButtonColor: '#4ABDBD',
      cancelButtonColor: '#e5e7eb',
      inputValidator: (v) => (!v || !v.trim() ? t('purchasereturns.note_required', 'A note number is required') : undefined),
    });
    if (!ask.isConfirmed) return;
    try {
      await axiosInstance.post(`/pharmacy/purchase-returns/${row.returnId}/settle`, {
        debitNoteNo: ask.value, settledAmount: num(row.claimAmount),
      });
      toast.success(t('purchasereturns.settled', 'Claim settled'));
      loadReturns();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('purchasereturns.settle_failed', 'Could not settle'));
    }
  };

  return (
    <div className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full" data-testid="purchase-returns-page">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
        <div className="ml-2">
          <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
            {t('purchasereturns.title', 'Purchase Returns')}
          </h1>
          <p className="text-xs text-slate-400 mt-1">
            {t('purchasereturns.subtitle', 'Claim expiring stock back from the distributor before the window closes.')}
          </p>
        </div>
        {tab === 'expiring' && Object.keys(picked).length > 0 && mayReturn && (
          <button
            data-testid="raise-claim"
            onClick={raiseClaim}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg font-semibold text-sm disabled:opacity-60 cursor-pointer whitespace-nowrap"
          >
            <IndianRupee className="w-4 h-4" />
            {t('purchasereturns.raise_n', {
              defaultValue: 'Raise claim · {{n}} batch · {{v}}',
              n: Object.keys(picked).length, v: money(pickedTotal),
            })}
          </button>
        )}
      </div>

      <div className="mb-6 flex flex-col sm:flex-row gap-2 sm:items-center">
        <div className="flex bg-gradient-to-r from-gray-50 to-blue-50/30 p-1 rounded-2xl shadow border border-gray-200 gap-2">
          {[['expiring', t('purchasereturns.tab_expiring', 'Expiring stock')], ['register', t('purchasereturns.tab_register', 'Claims raised')]].map(([k, label]) => (
            <button key={k} data-testid={`returns-tab-${k}`} onClick={() => setTab(k)}
              className={`px-5 py-2 text-sm rounded-xl transition-colors ${tab === k ? 'bg-gradient-to-r from-[#4ABDBD] to-[#2D8A8A] text-white shadow' : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'}`}>
              {label}
            </button>
          ))}
        </div>

        {tab === 'expiring' && (
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="expiring-window"
            className="tt-select p-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]">
            {[30, 60, 90, 180].map((d) => (
              <option key={d} value={d}>{t('purchasereturns.within_days', { defaultValue: 'Expiring within {{d}} days', d })}</option>
            ))}
          </select>
        )}

        <div className="relative w-full sm:w-72">
          <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="returns-search"
            placeholder={t('purchasereturns.search', 'Search medicine / batch / supplier…')}
            className="w-full p-2 pl-10 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]" />
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4ABDBD]" />
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-12"><CommonLoader /></div>
      ) : tab === 'expiring' ? (
        <div className="overflow-x-auto rounded-md border border-gray-200">
          <table className="w-full border-collapse" data-testid="expiring-table">
            <thead>
              <tr className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                <th className="p-3 text-left text-xs font-bold uppercase">{t('purchasereturns.claim', 'Claim')}</th>
                <th className="p-3 text-left text-xs font-bold uppercase">{t('purchasereturns.medicine', 'Medicine')}</th>
                <th className="p-3 text-left text-xs font-bold uppercase">{t('purchasereturns.batch', 'Batch')}</th>
                <th className="p-3 text-left text-xs font-bold uppercase">{t('purchasereturns.expiry', 'Expiry')}</th>
                <th className="p-3 text-left text-xs font-bold uppercase">{t('purchasereturns.on_hand', 'On hand')}</th>
                <th className="p-3 text-left text-xs font-bold uppercase">{t('purchasereturns.value', 'Value')}</th>
                <th className="p-3 text-left text-xs font-bold uppercase">{t('purchasereturns.supplier', 'Supplier')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length ? rows.map((b) => {
                const sel = !!picked[b.batchId];
                const daysLeft = b.daysToExpiry != null ? Number(b.daysToExpiry) : null;
                return (
                  <tr key={b.batchId} data-testid={`expiring-${b.batchId}`}
                    className={`border-b border-gray-100 ${sel ? 'bg-[#4ABDBD]/5' : 'hover:bg-blue-50/40'}`}>
                    <td className="p-3">
                      <input type="checkbox" checked={sel} onChange={() => toggle(b)} disabled={!mayReturn || b.claimRaised}
                        className="h-4 w-4 rounded border-gray-300 text-[#4ABDBD] focus:ring-[#4ABDBD] cursor-pointer" />
                    </td>
                    <td className="p-3 text-xs text-gray-700 font-semibold">{b.medicineName || '—'}</td>
                    <td className="p-3 text-xs text-gray-600">{b.batchNo}</td>
                    <td className="p-3 text-xs">
                      <span className={`font-semibold ${daysLeft != null && daysLeft < 30 ? 'text-red-600' : 'text-amber-600'}`}>
                        {dateOnly(b.expiryDate)}
                      </span>
                      {daysLeft != null && (
                        <div className="text-[11px] text-slate-400">
                          {daysLeft < 0
                            ? t('purchasereturns.expired', 'expired')
                            : t('purchasereturns.days_left', { defaultValue: '{{d}} days left', d: daysLeft })}
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-xs text-gray-600">
                      {sel ? (
                        <input type="number" min="0" max={num(b.qtyOnHand)} value={picked[b.batchId].qty}
                          onChange={(e) => setQty(b.batchId, e.target.value)}
                          className="w-20 px-2 py-1 border border-gray-300 rounded-lg text-xs" />
                      ) : num(b.qtyOnHand)}
                    </td>
                    <td className="p-3 text-xs text-gray-700">{money(num(b.qtyOnHand) * num(b.purchaseRate))}</td>
                    <td className="p-3 text-xs text-gray-600">
                      {b.supplierName || '—'}
                      {b.claimRaised && (
                        <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                          <Check className="w-3 h-3" />{t('purchasereturns.claimed', 'claimed')}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              }) : (
                <tr><td colSpan={7} className="p-8 text-center text-gray-500 text-sm">
                  <PackageX className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  {t('purchasereturns.none_expiring', 'Nothing expiring in this window. Widen it to look further ahead.')}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-gray-200">
          <table className="w-full border-collapse" data-testid="returns-table">
            <thead>
              <tr className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                <th className="p-3 text-left text-xs font-bold uppercase">{t('purchasereturns.no', 'Return no')}</th>
                <th className="p-3 text-left text-xs font-bold uppercase">{t('purchasereturns.date', 'Date')}</th>
                <th className="p-3 text-left text-xs font-bold uppercase">{t('purchasereturns.supplier', 'Supplier')}</th>
                <th className="p-3 text-left text-xs font-bold uppercase">{t('purchasereturns.reason', 'Reason')}</th>
                <th className="p-3 text-left text-xs font-bold uppercase">{t('purchasereturns.amount', 'Claim')}</th>
                <th className="p-3 text-left text-xs font-bold uppercase">{t('purchasereturns.status', 'Status')}</th>
                <th className="p-3 text-center text-xs font-bold uppercase">{t('purchasereturns.action', 'Action')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length ? rows.map((r) => (
                <tr key={r.returnId} data-testid={`return-${r.returnId}`} className="border-b border-gray-100 hover:bg-blue-50/40">
                  <td className="p-3 text-xs font-semibold text-gray-700">{r.returnNo}</td>
                  <td className="p-3 text-xs text-gray-600">{dateOnly(r.returnDate)}</td>
                  <td className="p-3 text-xs text-gray-600">{r.supplierName || '—'}</td>
                  <td className="p-3 text-xs text-gray-600">
                    {REASONS.find((x) => x.value === r.reason)?.label || r.reason}
                  </td>
                  <td className="p-3 text-xs text-gray-700">{money(r.claimAmount)}</td>
                  <td className="p-3 text-xs">
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_STYLE[r.status] || 'bg-gray-100 text-gray-600'}`}>
                      {r.status}
                    </span>
                    {r.debitNoteNo && <div className="text-[11px] text-slate-400 mt-1">{r.debitNoteNo}</div>}
                  </td>
                  <td className="p-3 text-xs">
                    <div className="flex justify-center gap-2">
                      {r.status === 'posted' && mayReturn && (
                        <>
                          <button onClick={() => settle(r)} data-testid={`settle-${r.returnId}`}
                            className="px-3 py-1.5 rounded-lg border border-[#4ABDBD] text-[#2D8A8A] text-xs font-semibold hover:bg-[#4ABDBD]/10 cursor-pointer">
                            {t('purchasereturns.settle', 'Settle')}
                          </button>
                          <button onClick={() => cancelReturn(r)} data-testid={`cancel-${r.returnId}`}
                            title={t('purchasereturns.cancel_hint', 'Puts the stock back')}
                            className="px-3 py-1.5 rounded-lg border border-red-200 text-red-500 text-xs font-semibold hover:bg-red-50 cursor-pointer">
                            <RotateCcw className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                      {r.status === 'settled' && (
                        <span className="text-[11px] text-green-700 inline-flex items-center gap-1">
                          <Check className="w-3.5 h-3.5" />{t('purchasereturns.credited', 'credited')}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={7} className="p-8 text-center text-gray-500 text-sm">
                  {t('purchasereturns.no_returns', 'No claims raised yet.')}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'expiring' && rows.length > 0 && (
        <p className="text-[11px] text-slate-400 mt-4 flex items-start gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-500" />
          {t('purchasereturns.window_note', 'Distributors close their credit window before the expiry date, not on it. Stock claimed late is bought twice.')}
        </p>
      )}
    </div>
  );
}
